import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router-dom";
import type {HomepageCarouselSlide, I18nKey} from "@corporatesclub/shared";
import {normalizeShopHref} from "@/lib/paths";
import {useI18n} from "./I18nProvider";
import {HOMEPAGE_HERO_SLIDES, type HomepageHeroSlide} from "./homepageCarouselSlides";

const AUTOPLAY_MS = 10000;
const SWIPE_THRESHOLD = 48;

type SlideInput = HomepageHeroSlide | HomepageCarouselSlide;

function isHeroSlide(slide: SlideInput): slide is HomepageHeroSlide {
  return "titleKey" in slide;
}

type ResolvedSlide = {
  key: string;
  image: string;
  url: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  cta: string;
};

function resolveSlides(slides: SlideInput[], t: (key: I18nKey) => string): ResolvedSlide[] {
  return slides.map((slide, index) => {
    if(isHeroSlide(slide)) {
      return {
        key: slide.url || String(index),
        image: slide.image,
        url: normalizeShopHref(slide.url),
        eyebrow: t(slide.eyebrowKey),
        title: t(slide.titleKey),
        subtitle: t(slide.subtitleKey),
        cta: t(slide.ctaKey)
      };
    }
    const url = slide.url && slide.url !== "#" ? normalizeShopHref(slide.url) : "/";
    return {
      key: url || String(index),
      image: slide.imagine,
      url,
      eyebrow: "",
      title: slide.nume,
      subtitle: "",
      cta: t("See all")
    };
  });
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

export function HomepageCarousel({
  slides = HOMEPAGE_HERO_SLIDES
}: {
  slides?: SlideInput[];
}) {
  const {t} = useI18n();
  const resolved = useMemo(() => resolveSlides(slides, t), [slides, t]);
  const reducedMotion = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const pointerStart = useRef<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const goPrev = useCallback(() => {
    setIdx((i) => (i - 1 + resolved.length) % resolved.length);
  }, [resolved.length]);

  const goNext = useCallback(() => {
    setIdx((i) => (i + 1) % resolved.length);
  }, [resolved.length]);

  useEffect(() => {
    setIdx(0);
  }, [resolved.length]);

  useEffect(() => {
    if(resolved.length <= 1 || reducedMotion) {
      setProgress(0);
      return;
    }
    setProgress(0);
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const pct = Math.min(100, ((now - started) / AUTOPLAY_MS) * 100);
      setProgress(pct);
      if(pct >= 100) return;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    const timer = setTimeout(goNext, AUTOPLAY_MS);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [idx, resolved.length, reducedMotion, goNext]);

  useEffect(() => {
    const stage = stageRef.current;
    if(!stage || resolved.length <= 1) return;
    const onPointerDown = (e: PointerEvent) => {
      if(e.pointerType === "mouse" && e.button !== 0) return;
      pointerStart.current = e.clientX;
    };
    const onPointerUp = (e: PointerEvent) => {
      if(pointerStart.current === null) return;
      const diff = e.clientX - pointerStart.current;
      pointerStart.current = null;
      if(Math.abs(diff) < SWIPE_THRESHOLD) return;
      if(diff < 0) goNext();
      else goPrev();
    };
    const onPointerCancel = () => { pointerStart.current = null; };
    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerCancel);
    return () => {
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [resolved.length, goNext, goPrev]);

  if(!resolved.length) return null;

  const rootClass = "cc-heroCarousel";

  return (
    <div id="slider" className={rootClass}>
      <div className="cc-heroCarousel__stage" ref={stageRef}>
        {resolved.map((slide, i) => (
          <Link
            key={slide.key}
            to={slide.url}
            className={`cc-heroCarousel__slide anim${i === idx ? " isActive" : ""}`}
            aria-hidden={i !== idx}
            tabIndex={i === idx ? 0 : -1}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="cc-heroCarousel__img"
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
            />
            <span className="cc-heroCarousel__overlay" aria-hidden="true" />
            <span className="cc-heroCarousel__copy">
              {slide.eyebrow ? <span className="cc-heroCarousel__eyebrow">{slide.eyebrow}</span> : null}
              <span className="cc-heroCarousel__title">{slide.title}</span>
              {slide.subtitle ? <b className="cc-heroCarousel__subtitle">{slide.subtitle}</b> : null}
              <span className="cc-heroCarousel__cta">{slide.cta}</span>
            </span>
          </Link>
        ))}
      </div>
      {resolved.length > 1 ? (
        <>
          {!reducedMotion ? (
            <div className="cc-heroCarousel__progress" aria-hidden="true">
              <span className="cc-heroCarousel__progressBar" style={{width: `${progress}%`}} />
            </div>
          ) : null}
          <div className="cc-heroCarousel__dots" role="tablist" aria-label={t("Carousel navigation")}>
            {resolved.map((slide, i) => (
              <button
                key={slide.key}
                type="button"
                role="tab"
                aria-selected={i === idx}
                aria-label={slide.title}
                className={`cc-heroCarousel__dot${i === idx ? " isActive" : ""}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
          <div className="owl-nav">
            <button type="button" className="owl-prev" aria-label={t("Previous")} onClick={goPrev}>
              <i className="fa fa-angle-left" aria-hidden="true" />
            </button>
            <button type="button" className="owl-next" aria-label={t("Next")} onClick={goNext}>
              <i className="fa fa-angle-right" aria-hidden="true" />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
